/**
 * Licensed Materials - Property of IBM
 * IBM Business Analytics (C) Copyright IBM Corp. 2019, 2024
 * US Government Users Restricted Rights - Use, duplication or disclosure restricted by GSA ADP Schedule Contract with IBM Corp.
 */

/**
 * @class SlideoutDOM
 * @hideconstructor
 * @classdesc API class that is used to manage Slideout functionality
 * (including the corrsponding view) across API calls
 */
define([
	'react',
	'react-dom',
	'../../../lib/@waca/dashboard-common/dist/core/APIFactory',
	'./InAppSlideoutDOMAPI',
	'./InAppSlideoutContainer'
], function(React, ReactDOM, APIFactory, InAppSlideoutDOMAPI, InAppSlideoutContainer) {

	class InAppSlideoutDOM {
		constructor(options) {
			this._slideout = options.features['InAppSlideoutState'];
			this._dashboard = options.features['API'];
			this._api = null;
			this._rootNode = null;
			this._currentView = null;
			this._views = [];
			this._domListeners = [];
			this._features = options.features;
			this._boundOnTransitionEnd = this._onTransitionEnd.bind(this);

			this._api = APIFactory.createAPI(this, [InAppSlideoutDOMAPI]);
			this._slideout.onStateChange(this._onSlideoutStateChange.bind(this));
			this._dashboard.getCanvasWhenReady().then(this._dashboardFrameInit.bind(this));

		}

		_dashboardFrameInit() {
			const dashboardContainer = this._getDashboardFrame();
			const slideout = dashboardContainer.querySelector('.dashboardFrameRight');
			slideout.classList.add('dashboardAuthoringToolsPane');
		}

		getAPI() {
			return this._api;
		}

		_onTransitionEnd(e) {
			if (e.target === this._rootNode) {
				const slideoutClosed = e.target.style.width === '0px';
				if (slideoutClosed) {
					this._unmountContribution();
					this._rootNode && this._rootNode.removeEventListener('transitionend', this._boundOnTransitionEnd);
					this._rootNode && ReactDOM.unmountComponentAtNode(this._rootNode);
					this._rootNode = null;
				}
				this._dispatchResizeEvent();
			}
		}

		_getDashboardFrame() {
			// TODO: get the canvas node from a feature. for now will do; RTC: 295170
			let dashboardRootContainer = document.querySelector('div.appview.paneColumn:not(.hidden)');
			if (!dashboardRootContainer) {
				dashboardRootContainer = document.body;
			}

			return dashboardRootContainer.querySelector('.dashboardFrame');
		}

		_onSlideoutStateChange(state = {}) {
			const { isOpen } = state.sidePanel;
			if (this._shouldRenderSlideout(state)) {
				this.render();
			} else if (!isOpen && this._currentView && this._rootNode) {
				// close the side panel if the state is closed, while a current view exists
				if (this._currentView.close) {
					this._currentView.close();
				}
				// do animation - slide out, width will become 0px
				this._rootNode.style = 'width: 0px';
			}
		}

		_unmountContribution() {
			if (this._currentView && this._currentView.unmount) {
				if (this._currentView.unmount) {
					this._currentView.unmount();
				}
				if (this._currentView.close) {
					this._currentView.close();
				}
			}
			this._currentView = null;
		}

		_dispatchResizeEvent() {
			const resizeEvent = new Event('resize', { bubbles: false, cancelable : true });
			window.dispatchEvent(resizeEvent);
		}

		registerView(id, instance) {
			const view = this.getView(id);
			if (view) {
				throw new Error(`View with id "${id}" is already registered`);
			}
			this._views.push({
				id,
				instance
			});
		}

		removeView(id) {
			const index = this._views.findIndex((item) => item.id === id);
			if (index === -1) {
				throw new Error(`View with id "${id}" does not exist`);
			}
			this._views.splice(index, 1);
		}

		getView(id) {
			return this._views.find(item => item.id === id);
		}

		getViews() {
			return this._views;
		}

		_setupRenderNode() {
			if (this._rootNode) {
				return;
			}

			const dashboardContainer = this._getDashboardFrame();
			this._rootNode = dashboardContainer.querySelector('.dashboardFrameRight');
			this._rootNode.classList.add('dashboardAuthoringToolsPane');
			this._rootNode.addEventListener('transitionend', this._boundOnTransitionEnd);

			// Get information to perform the "sliding" animation.
			const customPanelWidth = this._features['DashboardSettings'].get('panelWidth');
			const panelWidth = customPanelWidth ? customPanelWidth : '320px';

			const dashboardFrameCentre = dashboardContainer.querySelector('.dashboardFrameCentre');
			dashboardFrameCentre.setAttribute('style', `width: calc(100% - ${panelWidth})`);

			this._rootNode.style = `width: ${panelWidth}`;
		}

		render(options) {
			this._setupRenderNode();
			return new Promise((resolve, reject) => {
				try {
					const { id } = this._slideout.getCurrent();
					const { instance: contribution } = this.getView(id);
					if (contribution) {
						this._currentView = contribution;
						ReactDOM.render(
							<InAppSlideoutContainer
								active={this._slideout.isOpen()}
								contribution={contribution}
								options={options}
								onDomStateChange={this._onUiDomStateChange.bind(this)}
							/>,
							this._rootNode,
							() => resolve()
						);
					} else {
						resolve();
					}
				} catch(error) {
					reject(error);
				}
			});
		}

		destroy() {
			this._rootNode && this._rootNode.removeEventListener('transitionend', this._boundOnTransitionEnd);
			this._rootNode && ReactDOM.unmountComponentAtNode(this._rootNode);
			this._rootNode = null;
			this._api = null;
			this._currentView = null;
			this._domListeners.length = 0;
		}

		onDomStateChange(fn) {
			const newLength = this._domListeners.push(fn);
			const elementIndex = newLength - 1;
			const removeFn = () => {
				this._domListeners.splice(elementIndex, 1);
			};
			return removeFn;
		}

		/**
		 * Invoke registered views when the InAppSlideoutContainer component get mount/unmounted in the DOM
		 *
		 * @param {boolean} state.type - ['mounted' | 'unmounted']
		 * @param {string} [state.current] - the current contribution id
		 */
		_onUiDomStateChange (state) {
			if (!this._domListeners){
				return;
			}
			this._domListeners.forEach(fn => fn(state));
		}

		/**
		 * Returns a boolean indicating whether should render the slide out or not
		 * @param {object} state - the state provided by the DashboardState object
		 * @param {boolean} state.dirty - something changed in the dashboard marking this dashboard as dirty
		 * @param {boolean} state.selectionProperties - flag set by TimelineSliderView.js:openAnimationProperties
		 * @param {boolean} state.sidePanel.isOpen - flag use to open or close the panel
		 *
		 */
		_shouldRenderSlideout (state = {}) {
			const shouldRender = !this._currentView || this._currentView.getProviderId() !== state.sidePanel.current;
			return  state.sidePanel.isOpen && (shouldRender || state.dirty || state.selectionProperties);
		}
	}

	return InAppSlideoutDOM;
});
